import React, { useState } from 'react';
import { BikeIcon, CheckIcon, Loader2Icon, PencilIcon, PlusIcon, PowerIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { createExpressCourier, setExpressCourierActive, updateExpressCourier, type ExpressCourier } from '../../../lib/express-hooks';

// ──────────────────────────────────────────────────────────────────
// Catálogo de repartidores del Servicio Express.
//
// Los repartidores NUNCA se eliminan: solo se activan o desactivan. Un
// repartidor desactivado deja de aparecer al asignar, pero conserva su
// historial completo y sigue saliendo en los reportes del periodo — así
// ningún reporte queda con nombres en blanco.
// ──────────────────────────────────────────────────────────────────

interface ExpressCouriersSectionProps {
  restaurantId: number | string;
  couriers: ExpressCourier[];
  loading: boolean;
  onChanged: () => void;
}
interface FormState {
  id: number | null;
  name: string;
  phone: string;
}
const EMPTY_FORM: FormState = {
  id: null,
  name: '',
  phone: ''
};
export function ExpressCouriersSection({
  restaurantId,
  couriers,
  loading,
  onChanged
}: ExpressCouriersSectionProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const openNew = () => {
    setError(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };
  const openEdit = (courier: ExpressCourier) => {
    setError(null);
    setForm({
      id: courier.id,
      name: courier.name,
      phone: courier.phone || ''
    });
    setShowForm(true);
  };
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const result = form.id ? await updateExpressCourier({
      id: form.id,
      name: form.name,
      phone: form.phone
    }) : await createExpressCourier({
      restaurantId,
      name: form.name,
      phone: form.phone
    });
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'No se pudo guardar el repartidor.');
      return;
    }
    setShowForm(false);
    setForm(EMPTY_FORM);
    onChanged();
  };
  const handleToggle = async (courier: ExpressCourier) => {
    setTogglingId(courier.id);
    setError(null);
    const result = await setExpressCourierActive(courier.id, !courier.is_active);
    setTogglingId(null);
    if (!result.success) {
      setError(result.error || 'No se pudo actualizar el repartidor.');
      return;
    }
    onChanged();
  };
  const activeCount = couriers.filter(c => c.is_active).length;
  return <section aria-labelledby="express-couriers-heading" className="bg-white rounded-2xl border border-gray-200" data-id="element-791" data-mp="ayc4jvy4wfes">
      <header className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3" data-id="element-792" data-mp="600vhnfny8xm">
        <div className="flex items-center gap-2 min-w-0" data-id="element-793" data-mp="2hmce9c7hwjtc">
          <BikeIcon className="w-5 h-5 text-gray-700 shrink-0" data-id="element-794" data-mp="2hkrkvpwx36x2" />
          <div className="min-w-0" data-id="element-795" data-mp="2mevukgxkht1u">
            <h2 id="express-couriers-heading" className="font-bold text-gray-900" data-id="element-796" data-mp="28lwg8i9iu4vv">
              Repartidores
            </h2>
            <p className="text-xs text-gray-500" data-id="element-797" data-mp="2cnrflh7w6870">
              {activeCount} activo{activeCount === 1 ? '' : 's'} ·{' '}
              {couriers.length} registrado{couriers.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <button onClick={openNew} className="h-10 px-4 rounded-xl font-bold text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all flex items-center gap-2 shrink-0" data-id="element-798" data-mp="2rcnvu1k3qrip">
          <PlusIcon className="w-4 h-4" data-id="element-799" data-mp="1fwz7p6v43mgb" />
          Agregar
        </button>
      </header>

      {error && <div role="alert" className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-800" data-mp="1kxdaoahuw67h">
          {error}
        </div>}

      {showForm && <div className="mx-5 mt-4 p-4 rounded-xl border-2 border-gray-200 bg-gray-50" data-id="element-801" data-mp="1vegtr1d3zvp7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-802" data-mp="1qh73xklt5i0u">
            <div data-id="element-803" data-mp="1meu1b52cy08a">
              <label htmlFor="courier-name" className="block text-xs font-bold text-gray-700 mb-1" data-id="element-804" data-mp="1mfd7xpmk5270">
                Nombre <span className="text-red-500" data-id="element-805" data-mp="2j2gsookkqsev">*</span>
              </label>
              <input id="courier-name" type="text" value={form.name} autoFocus onChange={e => setForm(f => ({
            ...f,
            name: e.target.value
          }))} placeholder="Ej: Juan Rodríguez" className="w-full h-11 px-3 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:border-gray-900" data-id="element-806" data-mp="2e69n4ww3bixg" />
            </div>
            <div data-id="element-807" data-mp="35eb7ej3m6g13">
              <label htmlFor="courier-phone" className="block text-xs font-bold text-gray-700 mb-1" data-id="element-808" data-mp="35et6dnhnv8cp">
                Teléfono
              </label>
              <input id="courier-phone" type="tel" value={form.phone} onChange={e => setForm(f => ({
            ...f,
            phone: e.target.value
          }))} placeholder="Opcional" className="w-full h-11 px-3 rounded-lg border border-gray-300 bg-white text-sm tabular-nums focus:outline-none focus:border-gray-900" data-id="element-809" data-mp="3ecddve64a0vr" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3" data-id="element-810" data-mp="5x3e4cpp97r5">
            <button onClick={handleSave} disabled={saving || !form.name.trim()} className="h-10 px-4 rounded-lg font-bold text-sm bg-gray-900 hover:bg-gray-800 text-white disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all flex items-center gap-2" data-id="element-811" data-mp="5vs8068s7nwv">
              {saving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-812" data-mp="362vg7ag9g6ty" /> : <CheckIcon className="w-4 h-4" data-id="element-813" data-mp="3k24ug4kwoqva" />}
              {form.id ? 'Guardar' : 'Agregar'}
            </button>
            <button onClick={() => {
          setShowForm(false);
          setForm(EMPTY_FORM);
          setError(null);
        }} disabled={saving} className="h-10 px-4 rounded-lg font-bold text-sm bg-white border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-40 active:scale-95 transition-all" data-id="element-814" data-mp="3fy6zm62m6281">
              Cancelar
            </button>
          </div>
        </div>}

      <div className="p-5" data-id="element-815" data-mp="gl4rtj9rx8ex">
        {loading ? <div className="flex justify-center py-10 text-gray-400" data-id="element-816" data-mp="bljufda9584b">
            <Loader2Icon className="w-6 h-6 animate-spin" data-id="element-817" data-mp="bj74acucbrg6" />
          </div> : couriers.length === 0 ? <div className="text-center py-10" data-id="element-818" data-mp="v591juh04w3x">
            <BikeIcon className="w-10 h-10 mx-auto mb-3 text-gray-300" data-id="element-819" data-mp="1053rnvsydhnx" />
            <p className="font-bold text-gray-900" data-id="element-820" data-mp="m6nhfuvvt911">
              Todavía no hay repartidores
            </p>
            <p className="text-sm text-gray-500 mt-1 mb-4" data-id="element-821" data-mp="q7fr2p8o1fxu">
              Registrá al primero para poder asignarle entregas.
            </p>
            <button onClick={openNew} className="h-10 px-4 rounded-xl font-bold text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all inline-flex items-center gap-2" data-id="element-822" data-mp="cf9e1c0t7p2m">
              <PlusIcon className="w-4 h-4" data-id="element-823" data-mp="7hhd5si6ssma" />
              Agregar repartidor
            </button>
          </div> : <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2" data-id="element-824" data-mp="3pmucjd08dxy7">
            {couriers.map(courier => <li key={courier.id} className={cn('p-3 rounded-xl border flex items-center justify-between gap-3', courier.is_active ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50')} data-id="element-825" data-mp="3ulnqf6jfjqyw">
                <div className="min-w-0" data-id="element-826" data-mp="3w4fms6gxt2tg">
                  <p className={cn('font-bold text-sm truncate', courier.is_active ? 'text-gray-900' : 'text-gray-500')} data-id="element-827" data-mp="3w3e014qk6rvw">
                    {courier.name}
                  </p>
                  <p className="text-xs text-gray-500 tabular-nums truncate" data-id="element-828" data-mp="3herkp30xm04o">
                    {courier.phone || 'Sin teléfono'}
                    {!courier.is_active && ' · Desactivado'}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0" data-id="element-829" data-mp="38ebli5r81f5m">
                  <button onClick={() => openEdit(courier)} className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 flex items-center justify-center active:scale-95 transition-all" aria-label={`Editar ${courier.name}`} data-id="element-830" data-mp="33fi5a5kjva1r">
                    <PencilIcon className="w-4 h-4" data-id="element-831" data-mp="1ryxbld9dm8v7" />
                  </button>
                  <button onClick={() => handleToggle(courier)} disabled={togglingId === courier.id} className={cn('w-9 h-9 rounded-lg border flex items-center justify-center active:scale-95 transition-all disabled:opacity-40', courier.is_active ? 'border-gray-200 hover:bg-amber-50 hover:border-amber-200 text-amber-600' : 'border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 text-emerald-600')} aria-label={courier.is_active ? `Desactivar ${courier.name}` : `Reactivar ${courier.name}`} data-id="element-832" data-mp="1wwfp7gyc9c9j">
                    {togglingId === courier.id ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-833" data-mp="3ibxr3n1svxag" /> : courier.is_active ? <PowerIcon className="w-4 h-4" data-id="element-834" data-mp="3nbk72490jrmy" /> : <RotateCcwIcon className="w-4 h-4" data-id="element-835" data-mp="2pna4no5bf1vb" />}
                  </button>
                </div>
              </li>)}
          </ul>}
        {couriers.length > 0 && <p className="text-xs text-gray-400 mt-4 flex items-start gap-1.5" data-id="element-836" data-mp="2zkxwo6bjdv9s">
            <XIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-837" data-mp="34c69lcc2ryrp" />
            Los repartidores no se eliminan. Al desactivar uno, deja de
            aparecer al asignar pero conserva su historial y sus totales.
          </p>}
      </div>
    </section>;
}